import React from 'react'
import { reduxForm } from 'redux-form'
import { useTracking } from 'react-tracking'
import { sanitize } from 'dompurify'

import BEMHelper from '@ynap/bem-helper'
import { Notification } from '@ynap/notification'
import { SimpleAsyncButton } from '@ynap/button'
import Field, { InputGroup, Input } from '@ynap/field'
import { CombinedSelectWithCustomValue } from '@ynap/select'
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils'

import { trackEditPersonalInformation } from '../analytics'
import collateErrors from './collateErrors'
import validate from './validate'
import { getTitleOptions } from '../../../utils/getTitleOptions'
import type { PersonalInfoFormProps } from './types'

const bem = new BEMHelper('PersonalInfoForm')

export const PersonalInfoForm = (props: PersonalInfoFormProps) => {
    const {
        error,
        form,
        className,
        messages,
        children,
        submitting,
        handleSubmit,
        locale: { language, country },
        enableTitles
    } = props

    const { trackEvent } = useTracking()

    const submit = (values) => {
        const { form, onSubmit } = props
        trackEvent(trackEditPersonalInformation())
        const sanitizedValues = {
            ...values,
            ...(values.firstName ? {firstName: sanitize(values.firstName, {ALLOWED_TAGS: []})} : {}),
            ...(values.lastName ? {lastName: sanitize(values.lastName, {ALLOWED_TAGS: []})} : {})
        }
        return submitAndCollateErrorsPromise(onSubmit(sanitizedValues, form), sanitizedValues, props)
    }

    const withMonthFirst = country === 'US'

    return (
        <form id={form} className={bem(null, null, className)} onSubmit={handleSubmit(submit)}>
            {enableTitles ? (
                <Field
                    component={CombinedSelectWithCustomValue}
                    className={bem('title')}
                    name="personTitle"
                    label={messages.personalInfoForm.personTitle()}
                    emptyLabel={messages.personalInfoForm.selectTitle()}
                    autoComplete="honorific-prefix"
                    options={[...getTitleOptions(messages)]}
                />
            ) : null}
            <Field
                name="firstName"
                label={messages.personalInfoForm.firstName()}
                className={bem('firstName', language)}
            />
            <Field
                name="lastName"
                label={messages.personalInfoForm.lastName()}
                className={bem('lastName', language)}
            />
            <Field
                className={bem('dateOfBirthGroup', language)}
                component={InputGroup}
                name="dateOfBirthGroup"
                label={messages.personalInfoForm.dob()}
                help={messages.personalInfoForm?.dobHelp({ country })
                }
            >
                {withMonthFirst ? <Input className={bem('dateOfBirthItem')} name="month" maxLength="2" placeholder={messages?.personalInfoForm.monthPlaceholder()} /> : null}
                <Input className={bem('dateOfBirthItem')} name="day" maxLength="2" placeholder={messages?.personalInfoForm.dayPlaceholder()} />
                { !withMonthFirst ? <Input className={bem('dateOfBirthItem')} name="month" maxLength="2" placeholder={messages?.personalInfoForm.monthPlaceholder()} /> : null }
                <Input
                    className={bem('dateOfBirthItem')}
                    name="year"
                    maxLength="4"
                    placeholder={messages.personalInfoForm.yearPlaceholder()}
                />
            </Field>

            <div className={bem('buttonWrapper', language)}>
                <SimpleAsyncButton
                    className={bem('submit', language)}
                    copy={messages.personalInfoForm.submit()}
                    loading={submitting}
                />
            </div>

            {error ? <Notification type="error" message={error} className={bem('error')} /> : null}

            {children}
        </form>
    )
}

export default reduxForm({
    form: 'personalInfo',
    validate,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    collateErrors,
    onSubmitFail
})(PersonalInfoForm)
